/**
 * Staff and roles (iam) contracts: sign-in, sessions, overrides, TOTP, me, staff,
 * roles, the permission catalogue, and iam's events (03-modules.md §2,
 * 05-api.md "Auth and staff").
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import {
  deviceSecretSchema,
  emailSchema,
  loginIdentifierSchema,
  newPasswordSchema,
  newPinSchema,
  otpCodeSchema,
  passwordSchema,
  phoneSchema,
  pinSchema,
  recoveryCodeSchema,
} from './identity.js';
import { uuidSchema } from './ids.js';
import {
  assignablePermissionKeySchema,
  isOverridablePermission,
  PERMISSION_GROUPS,
  PERMISSION_SCOPES,
  permissionKeySchema,
} from './permissions.js';

// ---------------------------------------------------------------------------
// Sessions and sign-in

/**
 * Where a session may act (decided in M1 task 6):
 * - `till`: signed in on a registered till terminal (role pos, captain, kds, hub,
 *   kiosk) with a password, OTP or PIN. Only `pos`-scope permissions apply; no 2FA.
 * - `backoffice`: signed in from a browser (no terminal) or a `backoffice`
 *   terminal. All of the role's permissions apply; roles with `requireTotp`
 *   (Owner, Manager) and platform staff must pass TOTP.
 */
export const SESSION_SURFACES = ['till', 'backoffice'] as const;
export type SessionSurface = (typeof SESSION_SURFACES)[number];

export const AUTH_METHODS = ['password', 'otp', 'pin'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

/**
 * The terminal a sign-in happens on: the ids it got at registration and the device
 * secret it generated then (`POST {o}/terminals/register`).
 */
export const terminalCredentialSchema = z.object({
  companyId: uuidSchema,
  outletId: uuidSchema,
  terminalId: uuidSchema,
  secret: deviceSecretSchema,
});
export type TerminalCredential = z.output<typeof terminalCredentialSchema>;

export const loginRequestSchema = z.object({
  identifier: loginIdentifierSchema,
  password: passwordSchema,
  /** Required for back-office sign-in when the user has TOTP enabled. */
  totpCode: otpCodeSchema.optional(),
  /** Instead of `totpCode` when the authenticator is lost: one of the recovery codes (works once). */
  recoveryCode: recoveryCodeSchema.optional(),
  /** Needed only when the user works for more than one company. */
  companyId: uuidSchema.optional(),
  /** Present when signing in on a registered terminal (binds the session to it). */
  terminal: terminalCredentialSchema.optional(),
});

export const otpRequestSchema = z.object({ identifier: loginIdentifierSchema });

export const otpRequestResultSchema = z.object({
  /** Always true: the answer does not say whether an account exists. */
  accepted: z.literal(true),
  channel: z.enum(['sms', 'email']),
  expiresInSeconds: z.int().min(1),
  resendAfterSeconds: z.int().min(0),
});

export const otpVerifyRequestSchema = z.object({
  identifier: loginIdentifierSchema,
  code: otpCodeSchema,
  totpCode: otpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
  companyId: uuidSchema.optional(),
  terminal: terminalCredentialSchema.optional(),
});

export const pinLoginRequestSchema = z.object({
  terminal: terminalCredentialSchema,
  /** Picked from the PIN roster (`POST /v1/auth/pin/roster`). */
  userId: uuidSchema,
  pin: pinSchema,
});

/** The terminal asks, with its device secret, who can PIN in on it. */
export const pinRosterRequestSchema = z.object({ terminal: terminalCredentialSchema });

/** Who can PIN in at the terminal's outlet: names only (no contact details, no PIN material). */
export const pinRosterSchema = z.object({
  outletId: uuidSchema,
  staff: z.array(
    z.object({
      userId: uuidSchema,
      /** This company's name for the person. */
      name: z.string(),
      roleName: z.string(),
      /** PIN sign-in is locked for them at this outlet until then (wrong PINs). */
      pinLockedUntil: isoTimestampSchema.nullable(),
    }),
  ),
});

export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(20).max(512),
  /**
   * Required for a session bound to a terminal: the device secret it registered
   * with, so a refresh token copied off a till does not work anywhere else.
   */
  terminalSecret: deviceSecretSchema.optional(),
});

export const sessionInfoSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  companyId: uuidSchema,
  surface: z.enum(SESSION_SURFACES),
  authMethod: z.enum(AUTH_METHODS),
  terminalId: uuidSchema.nullable(),
  outletId: uuidSchema.nullable(),
  /** TOTP was checked at sign-in. */
  mfa: z.boolean(),
  /**
   * The user must enrol TOTP before this back-office session can do anything else:
   * only `POST /v1/auth/totp/enrol`, `/v1/auth/totp/verify`, `GET /v1/me` and
   * logout work. After enrolling, sign in again with the code.
   */
  totpEnrolmentRequired: z.boolean(),
  expiresAt: isoTimestampSchema,
});

export const tokenPairSchema = z.object({
  tokenType: z.literal('Bearer'),
  /** EdDSA JWT, 15 minutes. */
  accessToken: z.string(),
  accessTokenExpiresAt: isoTimestampSchema,
  /** Opaque, single use: each refresh returns a new one. Reusing an old one ends the session. */
  refreshToken: z.string(),
  refreshTokenExpiresAt: isoTimestampSchema,
  session: sessionInfoSchema,
});
export type TokenPair = z.output<typeof tokenPairSchema>;

export const ACTION_HASH_PATTERN = /^v1:[0-9a-f]{64}$/;

export const overrideRequestSchema = z.object({
  outletId: uuidSchema,
  /** The manager approving (picked from the outlet's staff list). */
  approverUserId: uuidSchema,
  pin: pinSchema,
  /** `requiredPermission` from the 403 OVERRIDE_REQUIRED problem. */
  permission: permissionKeySchema.refine(isOverridablePermission, {
    message: 'This permission cannot be approved by override',
  }),
  /** `actionHash` from the same problem. */
  actionHash: z.string().regex(ACTION_HASH_PATTERN),
});

export const overrideGrantSchema = z.object({
  overrideId: uuidSchema,
  /** Send as `X-Override-Token` with the identical request. Single use. */
  overrideToken: z.string(),
  permission: permissionKeySchema,
  approverUserId: uuidSchema,
  expiresAt: isoTimestampSchema,
});

export const totpEnrolmentSchema = z.object({
  /** Base32 secret for manual entry. Shown once. */
  secret: z.string(),
  otpauthUrl: z.string(),
  algorithm: z.literal('SHA1'),
  digits: z.literal(6),
  period: z.literal(30),
});

export const totpVerifyRequestSchema = z.object({ code: otpCodeSchema });
export const totpStatusSchema = z.object({
  enabled: z.boolean(),
  enabledAt: isoTimestampSchema,
  /**
   * One-time recovery codes for signing in without the authenticator (instead of
   * `totpCode`). Shown once; only digests are stored.
   */
  recoveryCodes: z.array(z.string()),
});

/** A second factor proving the person still controls TOTP: a current code or a recovery code. */
export const totpProofSchema = z
  .object({ totpCode: otpCodeSchema.optional(), recoveryCode: recoveryCodeSchema.optional() })
  .refine((v) => (v.totpCode === undefined) !== (v.recoveryCode === undefined), {
    message: 'Give either totpCode or recoveryCode',
    path: ['totpCode'],
  });

export const recoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });

export const changePasswordRequestSchema = z.object({
  /**
   * Required when the account already has a password, unless this session was
   * started with a one-time code (OTP) in the last 15 minutes: that is how a
   * forgotten password is reset.
   */
  currentPassword: passwordSchema.optional(),
  newPassword: newPasswordSchema,
});

// ---------------------------------------------------------------------------
// Me and the permission catalogue

export const meSchema = z.object({
  user: z.object({
    id: uuidSchema,
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    isPlatformStaff: z.boolean(),
    totpEnabled: z.boolean(),
    hasPassword: z.boolean(),
  }),
  companyId: uuidSchema,
  session: sessionInfoSchema,
  memberships: z.array(
    z.object({
      id: uuidSchema,
      /** null: company-wide (every outlet, including ones added later). */
      outletId: uuidSchema.nullable(),
      roleId: uuidSchema,
      roleName: z.string(),
    }),
  ),
  /** What this session may do at each outlet (navigation is built from this). */
  outlets: z.array(z.object({ outletId: uuidSchema, permissions: z.array(permissionKeySchema) })),
  /** Company-level permissions (routes outside /v1/outlets/{o}). */
  permissions: z.array(permissionKeySchema),
});

export const permissionCatalogueSchema = z.object({
  groups: z.array(z.object({ key: z.string(), label: z.string() })),
  permissions: z.array(
    z.object({
      key: permissionKeySchema,
      label: z.string(),
      group: z.enum(Object.keys(PERMISSION_GROUPS) as [string, ...string[]]),
      scope: z.enum(PERMISSION_SCOPES),
      overridable: z.boolean(),
      assignable: z.boolean(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Staff

export const USER_STATUSES = ['invited', 'active', 'disabled'] as const;
export const MEMBERSHIP_SCOPES = ['outlet', 'company'] as const;

export const staffMemberSchema = z.object({
  userId: uuidSchema,
  membershipId: uuidSchema,
  /** `company`: works at every outlet of the company (outletId null). */
  scope: z.enum(MEMBERSHIP_SCOPES),
  outletId: uuidSchema.nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  roleId: uuidSchema,
  roleName: z.string(),
  active: z.boolean(),
  status: z.enum(USER_STATUSES),
  /** Has a PIN at this outlet. */
  hasPin: z.boolean(),
  pinLockedUntil: isoTimestampSchema.nullable(),
  version: z.int(),
  createdAt: isoTimestampSchema,
});

export const inviteStaffSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    phone: phoneSchema.optional(),
    email: emailSchema.optional(),
    roleId: uuidSchema,
    /** `company` makes them staff at every outlet; needs `roles.manage`. Default `outlet`. */
    scope: z.enum(MEMBERSHIP_SCOPES).optional(),
  })
  .refine((v) => v.phone !== undefined || v.email !== undefined, {
    message: 'Give a phone number or an email address',
    path: ['phone'],
  });

export const updateStaffSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    roleId: uuidSchema.optional(),
    /** false deactivates (like DELETE); true reactivates. */
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Change at least one field',
  });

export const setPinSchema = z.object({ pin: newPinSchema });

export const staffQuerySchema = z.object({
  active: z
    .enum(['true', 'false'])
    .optional()
    .describe('Only active (true) or inactive (false) staff'),
});

export const staffParamsSchema = z.object({ outletId: uuidSchema, userId: uuidSchema });

// ---------------------------------------------------------------------------
// Onboarding a company (F1): its default roles and first Owner

/**
 * `POST /v1/admin/companies/{companyId}/owner` (QRkey staff): the new company's
 * first Owner, company-wide, while it has no staff yet. They sign in with a one-time
 * code to the email given here (no password is created or sent), then enrol TOTP.
 */
export const inviteOwnerSchema = z.object({
  name: z.string().trim().min(1).max(100),
  /** Optional contact detail; codes cannot be sent to a phone yet (see email). */
  phone: phoneSchema.optional(),
  /**
   * Required: the Owner signs in with a one-time code, and outside local
   * development codes go by email only until SMS/WhatsApp OTP connectors exist.
   */
  email: emailSchema,
});

export const companyParamsSchema = z.object({ companyId: uuidSchema });

export const companyOwnerSchema = z.object({
  companyId: uuidSchema,
  userId: uuidSchema,
  membershipId: uuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string(),
  roleId: uuidSchema,
  /** A new account was created (not an existing person made Owner here). */
  newUser: z.boolean(),
  /** Default roles this call created (`owner`, `manager`, …); empty when they existed. */
  createdRoles: z.array(z.string()),
});

/** What a registered till caches for offline PIN sign-in (08-clients.md, M4). */
export const offlinePinBundleSchema = z.object({
  outletId: uuidSchema,
  generatedAt: isoTimestampSchema,
  /**
   * PHC-format argon2id verifiers (`$argon2id$v=19$m=…,t=…,p=…$salt$hash`), separate
   * from the server's own PIN hashes (different salt), so the till can check a PIN
   * without the cloud.
   */
  algorithm: z.literal('argon2id'),
  staff: z.array(
    z.object({
      userId: uuidSchema,
      name: z.string(),
      roleId: uuidSchema,
      roleName: z.string(),
      offlineVerifier: z.string().nullable(),
      /** `pos` permissions at this outlet, for gating actions offline. */
      permissions: z.array(permissionKeySchema),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Roles

export const roleSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** `owner`, `manager`, … for the shipped default roles; null for custom ones. */
  systemKey: z.string().nullable(),
  requireTotp: z.boolean(),
  permissions: z.array(permissionKeySchema),
  archivedAt: isoTimestampSchema.nullable(),
  version: z.int(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
});

const roleName = z.string().trim().min(1).max(60);

export const createRoleSchema = z.object({
  name: roleName,
  description: z.string().trim().max(200).optional(),
  requireTotp: z.boolean().optional(),
  permissions: z.array(assignablePermissionKeySchema).max(500),
});

export const updateRoleSchema = z
  .object({
    name: roleName.optional(),
    description: z.string().trim().max(200).nullable().optional(),
    requireTotp: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Change at least one field',
  });

export const setRolePermissionsSchema = z.object({
  permissions: z.array(assignablePermissionKeySchema).max(500),
});

export const rolePermissionsSchema = z.object({
  roleId: uuidSchema,
  permissions: z.array(permissionKeySchema),
});

export const roleParamsSchema = z.object({ roleId: uuidSchema });

// ---------------------------------------------------------------------------
// Events

export const staffInvitedV1 = defineEvent(
  'staff.invited',
  1,
  z.object({
    userId: uuidSchema,
    membershipId: uuidSchema,
    outletId: uuidSchema.nullable(),
    roleId: uuidSchema,
    /** A new account was created (not an existing person added to this company). */
    newUser: z.boolean(),
  }),
);

export const staffUpdatedV1 = defineEvent(
  'staff.updated',
  1,
  z.object({
    userId: uuidSchema,
    membershipId: uuidSchema,
    changes: z.array(z.enum(['name', 'role', 'reactivated'])),
  }),
);

export const staffDeactivatedV1 = defineEvent(
  'staff.deactivated',
  1,
  z.object({ userId: uuidSchema, membershipId: uuidSchema, outletId: uuidSchema.nullable() }),
);

export const staffPinSetV1 = defineEvent(
  'staff.pin_set',
  1,
  z.object({ userId: uuidSchema, outletId: uuidSchema }),
);

export const roleChangedV1 = defineEvent(
  'role.changed',
  1,
  z.object({
    roleId: uuidSchema,
    change: z.enum(['created', 'updated', 'permissions', 'archived']),
    permissions: z.array(z.string()).optional(),
  }),
);

export const sessionStartedV1 = defineEvent(
  'session.started',
  1,
  z.object({
    sessionId: uuidSchema,
    userId: uuidSchema,
    surface: z.enum(SESSION_SURFACES),
    authMethod: z.enum(AUTH_METHODS),
    terminalId: uuidSchema.nullable(),
    mfa: z.boolean(),
  }),
);

export const sessionRefreshedV1 = defineEvent(
  'session.refreshed',
  1,
  z.object({ sessionId: uuidSchema, userId: uuidSchema }),
);

export const sessionEndedV1 = defineEvent(
  'session.ended',
  1,
  z.object({
    sessionId: uuidSchema,
    userId: uuidSchema,
    /**
     * `terminal_switched`: someone else signed in on the same terminal (PIN
     * switch), which ends the sessions bound to it before.
     */
    reason: z.enum(['logout', 'refresh_token_reused', 'terminal_switched']),
  }),
);

/**
 * A wrong PIN at a terminal (sign-in or override approval). `lockedUntil` is set
 * when this failure locked the person's PIN at the outlet; `terminalLocked` when
 * it stopped PIN entry on the terminal.
 */
export const staffPinFailedV1 = defineEvent(
  'staff.pin_failed',
  1,
  z.object({
    userId: uuidSchema,
    outletId: uuidSchema,
    terminalId: uuidSchema,
    purpose: z.enum(['login', 'override']),
    failedCount: z.int().min(0),
    lockedUntil: isoTimestampSchema.nullable(),
    terminalLocked: z.boolean(),
  }),
);

export const overrideGrantedV1 = defineEvent(
  'override.granted',
  1,
  z.object({
    overrideId: uuidSchema,
    permission: z.string(),
    approverUserId: uuidSchema,
    requestedByUserId: uuidSchema,
    terminalId: uuidSchema.nullable(),
    expiresAt: isoTimestampSchema,
  }),
);

export const totpEnrolmentStartedV1 = defineEvent(
  'totp.enrolment_started',
  1,
  z.object({ userId: uuidSchema }),
);

export const totpEnabledV1 = defineEvent('totp.enabled', 1, z.object({ userId: uuidSchema }));

export const totpDisabledV1 = defineEvent('totp.disabled', 1, z.object({ userId: uuidSchema }));

export const totpRecoveryCodesRenewedV1 = defineEvent(
  'totp.recovery_codes_renewed',
  1,
  z.object({ userId: uuidSchema }),
);

export const passwordChangedV1 = defineEvent(
  'user.password_changed',
  1,
  z.object({ userId: uuidSchema }),
);

export const IAM_EVENTS = [
  staffInvitedV1,
  staffUpdatedV1,
  staffDeactivatedV1,
  staffPinSetV1,
  roleChangedV1,
  sessionStartedV1,
  sessionRefreshedV1,
  sessionEndedV1,
  overrideGrantedV1,
  staffPinFailedV1,
  totpEnrolmentStartedV1,
  totpEnabledV1,
  totpDisabledV1,
  totpRecoveryCodesRenewedV1,
  passwordChangedV1,
] as const;
